import { Button, Col, Form, Row } from "antd";
import React, { useEffect, useRef } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";
import { SelectOption } from "../../../libs/types";
import { InputCheckbox, InputField, SelectCreateAbleField, SelectField } from "../../atoms/FormElement";
import {
  leaseTypeOptions,
  patternOptions,
  paymentModeOptions,
  paymentTypeOptions,
  usageTypeOptions,
} from "./selectOptions";
import { FormType, PaymentTypeFormData } from "./types";
import { useOrganizations } from "./useOrganizations";
import { useGetOrganization } from "../../../api/v1";
import { InputNumberField } from "../../atoms/FormElement/InputNumberField";

export type PaymentTypeFormProps = {
  methods: UseFormReturn<PaymentTypeFormData>;
  formType: FormType;
  onSubmit: (data: PaymentTypeFormData) => void;
  descriptionOptions?: SelectOption[];
};

export const PaymentTypeForm = (props: PaymentTypeFormProps) => {
  const { methods, formType, onSubmit, descriptionOptions } = props;

  const { organization } = useGetOrganization();
  const { organizationOptions } = useOrganizations();

  const { control, formState, handleSubmit } = methods;
  const { errors } = formState;

  const watchedOrganization = useWatch({ name: "organization", control: methods.control });
  const watchedUsageType = useWatch({ name: "usageType", control: methods.control });
  const watchedIsWriteOff = useWatch({ name: "isWriteOff", control: methods.control });
  const previousUsageType = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (watchedIsWriteOff) {
      methods.setValue("isUpfrontPayment", false, { shouldDirty: true, shouldValidate: true });
    }
  }, [methods, watchedIsWriteOff]);

  useEffect(() => {
    if (watchedOrganization?.value) return;
    methods.setValue("usageType", null);
    methods.setValue("description", null);
  }, [methods, watchedOrganization?.value]);

  useEffect(() => {
    const usageType = watchedUsageType?.value;

    if (previousUsageType.current === undefined) {
      previousUsageType.current = usageType;
      return;
    }

    if (previousUsageType.current !== usageType) {
      methods.setValue("description", null);
    }

    previousUsageType.current = usageType;
  }, [methods, watchedUsageType?.value]);

  return (
    <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
      <Row gutter={12}>
        <Col span={24}>
          <SelectField
            isRequired
            fieldname="organization"
            label="Organization"
            control={control}
            isDisabled={formType === "edit"}
            selectOption={organizationOptions}
            rules={{ required: "This field is required" }}
            iProps={{ placeholder: "Select Organization" }}
            validate={errors?.organization && "error"}
            validMessage={errors?.organization?.message}
          />
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={12}>
          <SelectField
            isRequired
            fieldname="usageType"
            label="Usage Type"
            control={control}
            selectOption={usageTypeOptions}
            isDisabled={!watchedOrganization?.value}
            rules={{ required: "This field is required" }}
            iProps={{ placeholder: "Enter Usage Type" }}
            validate={errors?.usageType && "error"}
            validMessage={errors?.usageType?.message}
          />
        </Col>
        <Col span={12}>
          <SelectCreateAbleField
            isRequired
            fieldname="description"
            label="Description"
            control={control}
            selectOption={descriptionOptions}
            isDisabled={!watchedOrganization?.value || !watchedUsageType?.value}
            rules={{ required: "This field is required" }}
            iProps={{ placeholder: "Enter Description" }}
            validate={errors?.description && "error"}
            validMessage={errors?.description?.message}
          />
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={12}>
          <InputNumberField
            isRequired
            label="VAT Rate (%)"
            fieldname="vatInPercentage"
            control={control}
            defaultValue={0}
            inputNumberProps={{
              placeholder: "Enter VAT Rate",
              min: 0,
              max: 100,
            }}
          />
        </Col>
        <Col span={12}>
          <SelectField
            isRequired
            fieldname="modeOfPayment"
            label="Payment Mode"
            control={control}
            selectOption={paymentModeOptions}
            rules={{ required: "This field is required" }}
            iProps={{ placeholder: "Select Payment Mode" }}
            validate={errors?.modeOfPayment && "error"}
            validMessage={errors?.modeOfPayment?.message}
          />
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={12}>
          <SelectField
            isRequired
            fieldname="paymentType"
            label="Payment Type"
            control={control}
            selectOption={paymentTypeOptions}
            rules={{ required: "This field is required" }}
            iProps={{ placeholder: "Enter Payment Type" }}
            validate={errors?.paymentType && "error"}
            validMessage={errors?.paymentType?.message}
          />
        </Col>
        <Col span={12}>
          <InputNumberField
            isRequired
            fieldname="defaultAmount"
            label="Amount / %"
            control={control}
            defaultValue={0}
            format="currency"
            inputNumberProps={{ placeholder: "Enter Amount", min: 0 }}
          />
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={12}>
          <SelectField
            isRequired
            fieldname="leaseType"
            label="Lease Type"
            control={control}
            selectOption={leaseTypeOptions}
            rules={{ required: "This field is required" }}
            iProps={{ placeholder: "Enter Lease Type" }}
            validate={errors?.leaseType && "error"}
            validMessage={errors?.leaseType?.message}
          />
        </Col>
        <Col span={12}>
          <SelectField
            isRequired
            fieldname="pattern"
            label="Pattern"
            control={control}
            selectOption={patternOptions}
            rules={{ required: "This field is required" }}
            iProps={{ placeholder: "Enter Pattern" }}
            validate={errors?.pattern && "error"}
            validMessage={errors?.pattern?.message}
          />
        </Col>
      </Row>
      <Row gutter={12} style={{ marginBottom: "1.5rem" }}>
        <Col flex="1">
          <InputCheckbox
            fieldname="isUpfrontPayment"
            label="Is Upfront Payment"
            control={control}
            initialValue={false}
            isDisabled={Boolean(watchedIsWriteOff)}
            validate={errors?.isUpfrontPayment && "error"}
            validMessage={errors?.isUpfrontPayment?.message}
          />
        </Col>
        {organization?.enableWriteOff && (
          <Col flex="1">
            <InputCheckbox
              fieldname="isWriteOff"
              label="Is Write Off"
              control={control}
              initialValue={false}
              validate={errors?.isWriteOff && "error"}
              validMessage={errors?.isWriteOff?.message}
            />
          </Col>
        )}
      </Row>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button className="ag-green-button theme_btn" htmlType="submit">
          Submit
        </Button>
      </div>
    </Form>
  );
};
